import { Skeleton } from '@mui/material';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { BarChart } from 'Components/BaseCharts/BarChart';
import { Button } from 'Components/Buttons/Buttons';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { Action, ActionStatus } from 'Models/Actions';
import { DateOptionSelectOption, DefaultUpcomingDateRangeOptions } from 'Models/Dashboards';
import { getMonths, getOpenActionsPerMonth } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import { ActionDisplayModal, ActionModalDisplayType } from './ActionDisplayModal';
import styles from './RiskRegisterDashboard.module.css';

enum Modals {
    NONE,
    CUSTOM_DATE,
    DISPLAY_ACTIONS_WITH_DUE_DATE,
    DISPLAY_ACTIONS_WITHOUT_DUE_DATE,
}

export interface ActionTrackingProps {
    allActions?: Action[];
    actionsError?: string;
}

export const ActionTracking = (props: ActionTrackingProps): JSX.Element => {
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);

    const actions = useMemo(() => props.allActions?.filter((action) => action.status === ActionStatus.OPEN), [props.allActions]);
    const actionsWithoutDueDate = useMemo(() => actions?.filter((action) => !action.due_date), [actions]);
    const [actionsByMonth, setActionsByMonth] = useState<number[]>();
    const [clickedActions, setClickedActions] = useState<Action[]>();
    const [clickedDate, setClickedDate] = useState<string>();
    const [monthLabel, setMonthLabel] = useState<string[]>();

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.NEXT_THREE_MONTHS);

    /** Update the chart data when the Actions are loaded or the date filter changes. */
    useEffect(() => {
        if (actions) {
            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const totalActionsByMonth = months.map((month) => {
                return actions.filter((action) => action.due_date !== undefined && moment(action.due_date).format('MMM YY') === month).length;
            });

            const overdueActions = actions.filter((action) => moment(action.due_date).isBefore(moment()));

            setActionsByMonth([overdueActions.length, ...totalActionsByMonth]);
            setMonthLabel(['Overdue', ...months]);
        }
    }, [actions, dateFilterHookValues]);

    /** Handler for when a bar in the chart is clicked. */
    const handleChartClick = (item: string) => {
        if (actions) {
            const clickedItem = item.split(' | ');
            const clickedDate = toTitleCase(clickedItem[0]);
            if (clickedDate === 'Overdue') {
                const overdueActions = actions.filter((action) => moment(action.due_date).isBefore(moment()));

                setClickedActions(overdueActions);
            } else {
                setClickedActions(getOpenActionsPerMonth(actions, clickedDate));
            }
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_ACTIONS_WITH_DUE_DATE);
        }
    };

    if (props.actionsError) {
        return (
            <>
                <div className={styles.dashletHeaderWithTitleWithFilter}>
                    <Text variant="Header2" color="white">
                        Action Tracking
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.actionsError}
                </Text>
            </>
        );
    } else {
        return (
            <>
                {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="futureDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
                {modalState === Modals.DISPLAY_ACTIONS_WITH_DUE_DATE && clickedDate && clickedActions && <ActionDisplayModal selectedActions={clickedActions} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} modalType={ActionModalDisplayType.OPEN} />}
                {modalState === Modals.DISPLAY_ACTIONS_WITHOUT_DUE_DATE && actionsWithoutDueDate && <ActionDisplayModal selectedActions={actionsWithoutDueDate} date="No due date" hideModal={() => setModalState(Modals.NONE)} modalType={ActionModalDisplayType.OPEN} />}
                <div className={styles.dashletHeaderWithTitleWithFilter}>
                    <Text variant="Header2" color="white">
                        Action Tracking
                    </Text>
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultUpcomingDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
                {monthLabel && actionsByMonth ? <BarChart onChartClick={handleChartClick} xAxisLabels={monthLabel} bars={{ name: 'Total Due', color: CSSColors.LIGHT_BLUE, data: actionsByMonth }} yAxisInfo={[{ name: 'Open Actions By Month', position: 'left', minInterval: 1 }]} /> : <Skeleton variant="rounded" width={'100%'} height={300} />}
                {actionsWithoutDueDate && actionsWithoutDueDate.length > 0 && (
                    <Button variant="linkText" size="sm" onClick={() => setModalState(Modals.DISPLAY_ACTIONS_WITHOUT_DUE_DATE)}>
                        {`${actionsWithoutDueDate.length} action${actionsWithoutDueDate.length === 1 ? '' : 's'} do${actionsWithoutDueDate.length === 1 ? 'es' : ''} not have a due date`}
                    </Button>
                )}
            </>
        );
    }
};
